import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import RawHeader from "../components/RawHeader";
import { InfoFooter } from "./InfoPages";

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | failed
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (tries.current >= 8) {
        setState("expired");
        return;
      }
      tries.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setInfo(r.data);
        if (r.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (r.data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen text-white">
      <RawHeader />
      <div className="max-w-2xl mx-auto px-6 py-20" data-testid="success-page">
        <div className="border border-zinc-800 bg-[#0a0a0a] p-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// TRANSACTION_STATUS</div>
          {state === "checking" && (
            <>
              <h1 className="font-display text-3xl mb-4">PROCESSING...</h1>
              <p className="text-sm text-zinc-400">Verifying payment with the network. This takes a few seconds.</p>
              <div className="mt-6 font-mono text-xs text-zinc-600">
                ATTEMPT {tries.current}/8 — SESSION: {sessionId?.slice(0, 16)}...
              </div>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="text-[#16a34a] font-mono text-xs tracking-[0.3em] uppercase mb-3">[ PAYMENT_CONFIRMED ]</div>
              <h1 className="font-display text-4xl mb-4">ORDER RECEIVED</h1>
              <p className="text-sm text-zinc-400 mb-6">
                Your order has been received. We'll email you a confirmation shortly and dispatch your order via Royal Mail / tracked international post.
              </p>
              {info && (
                <div className="font-mono text-xs text-zinc-500 space-y-1 border-l-2 border-[#d4af37] pl-4 mb-6">
                  <div>PRODUCT: {info.metadata?.product_name}</div>
                  <div>SIZE: {info.metadata?.size}</div>
                  <div>QTY: {info.metadata?.quantity}</div>
                  <div>TOTAL PAID: £{((info.amount_total || 0) / 100).toFixed(2)} {info.currency?.toUpperCase()}</div>
                  <div className="text-zinc-600 pt-2">VAT (20%) and shipping included in total above.</div>
                </div>
              )}
              <Link to="/" className="btn-raw">← BACK TO SHOP</Link>
            </>
          )}
          {state === "expired" && (
            <>
              <div className="text-[#d4af37] font-mono text-xs tracking-[0.3em] uppercase mb-3">[ TIMEOUT ]</div>
              <h1 className="font-display text-3xl mb-4">STATUS UNCERTAIN</h1>
              <p className="text-sm text-zinc-400 mb-6">
                Payment verification timed out. Check your email — we'll confirm shortly.
              </p>
              <Link to="/" className="btn-raw">← BACK TO SHOP</Link>
            </>
          )}
          {state === "failed" && (
            <>
              <div className="text-[#d4af37] font-mono text-xs tracking-[0.3em] uppercase mb-3">[ ERROR ]</div>
              <h1 className="font-display text-3xl mb-4">SESSION INVALID</h1>
              <p className="text-sm text-zinc-400 mb-6">No session ID received.</p>
              <Link to="/" className="btn-raw">← BACK TO SHOP</Link>
            </>
          )}
        </div>
      </div>
      <InfoFooter />
    </div>
  );
};

export default CheckoutSuccess;
